import { useKeycloak } from "@react-keycloak/web";
import { Button, Image } from "antd";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import CloseIcon from "../../../assets/icons/CloseIcon";
import UploadIcon from "../../../assets/icons/UploadIcon";
import { API_URL } from "../../../constants/apiUrl";
import { APP_API } from "../../../constants/constants";
import { setUserInfo, userLoggedIn } from "../../../features/auth/authSlice";
import { useUploadFileMutation } from "../../../features/portal/fileUpload/fileUpload";
import { useUpdateSingleUserMutation } from "../../../features/portal/user/userApi";
import useError from "../../../hooks/useError";
import useProfile from "../../../hooks/useProfile";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import CustomModal from "../../ui/CustomModal";

type AddUserPhotoModalProps = ModalComponentProps & {
  handleParentClose?: () => void;
};

export default function AddUserPhotoModal({
  handleClose,
  handleParentClose,
  ...rest
}: AddUserPhotoModalProps) {
  const [attachment, setAttachment] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { user } = useAppSelector((state) => state.auth);
  const { reFatch } = useProfile();

  const [uploadFile, { isLoading, isError, error }] = useUploadFileMutation();
  const [
    updateSingleUser,
    {
      isError: updateSingleUserIsError,
      error: updateSingleUserError,
      isSuccess: updateSingleUserIsSuccess,
      isLoading: updateSingleUserIsLoading,
    },
  ] = useUpdateSingleUserMutation();
  useError(updateSingleUserIsError, updateSingleUserError, "User updated fail");
  useError(isError, error, "File upload fail");

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxFileSize = 30 * 1024 * 1024;

    if (file.size > maxFileSize) {
      toast("error", `"${file.name}" exceeds max file size of 30MB.`);
      e.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    try {
      const res: any = await uploadFile(formData).unwrap();
      if (res?.Location) {
        setAttachment(res.Location);
      }
    } catch (err) {
      console.error(`Upload failed for ${file.name}`, err);
    }

    e.target.value = "";
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleUpdatePhoto = () => {
    const newData = {
      photo: attachment,
    };

    // console.log("Update Photo:", newData);

    updateSingleUser({
      id: user?.data?.id,
      data: newData,
    });
  };

  useEffect(() => {
    if (updateSingleUserIsSuccess) {
      toast("success", "User photo added successfully!");
      setAttachment("");
      handleClose();
      reFatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSingleUserIsSuccess]);

  const dispatch = useAppDispatch();
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = keycloak?.realmAccess?.roles?.includes("super_admin");
  const isAdmin =
    keycloak?.realmAccess?.roles?.includes("admin") ||
    keycloak?.realmAccess?.roles?.includes("admin2");

  useEffect(() => {
    console.log("abcd");
    if (keycloak?.authenticated && keycloak?.token) {
      if (keycloak?.token) {
        dispatch(userLoggedIn(keycloak?.token));

        // fetch(`${APP_API}${API_URL.getMe}/${keycloak?.tokenParsed?.email}`, {
        fetch(`${APP_API}${API_URL.getMe}`, {
          method: "GET",
          // mode: "no-cors",
          headers: {
            Authorization: `Bearer ${keycloak?.token}`,
            "ngrok-skip-browser-warning": "true",
          },
        })
          .then((res) => res.json())
          .then((data) => {
            console.log("data", data);
            dispatch(
              setUserInfo({
                ...data,
                data: {
                  ...data?.data,
                  permission: isSuperAdmin
                    ? "SUPER_ADMIN"
                    : isAdmin
                    ? "Admin"
                    : data?.data?.permission,
                },
              })
            );
            setLoading(false);
          })
          .catch((err) => {
            setLoading(false);
          });
      }
    } else {
      setTimeout(() => {
        setLoading(false);
        // removeSession();
      }, 5000);
    }
  }, [
    dispatch,
    keycloak?.authenticated,
    keycloak?.token,
    keycloak?.tokenParsed?.email,
    isSuperAdmin,
    isAdmin,
    updateSingleUserIsSuccess,
  ]);

  return (
    <CustomModal
      title="Add Photo"
      {...rest}
      handleClose={handleClose}
      width={360}
      footer={null}
    >
      <div className="flex flex-col items-center justify-center mt-4 gap-4">
        {attachment && (
          <div className="relative">
            <Image
              src={attachment}
              className="rounded-full object-cover"
              alt="Uploaded Photo"
              width={80}
              height={80}
            />
            <span
              className="absolute -top-1 -right-1 bg-white p-1 rounded-full shadow cursor-pointer"
              onClick={() => setAttachment("")}
            >
              <CloseIcon />
            </span>
          </div>
        )}

        <div>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleImageChange}
            accept="image/*"
          />
          <Button
            icon={<UploadIcon />}
            loading={isLoading}
            onClick={triggerFileInput}
          >
            Upload Photo
          </Button>
        </div>
      </div>

      <div className="py-2 flex justify-between">
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          loading={updateSingleUserIsLoading}
          onClick={handleUpdatePhoto}
          disabled={!attachment}
          className="bg-[#FFF1EB] text-[#FE7235] border-none"
        >
          Add
        </Button>
      </div>
    </CustomModal>
  );
}
